import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
// import ReactDOM from "react-dom";
import { API_URL } from "../config";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  IconButton,
  Grid,
  Chip,
  Divider,
  CircularProgress,
} from "@mui/material";
import {
  FaArrowLeft,
  FaUserGraduate,
  FaFileAlt,
  FaFilePdf,
  FaFileCsv,
  FaTimes,
  FaGraduationCap,
  FaExclamationTriangle,
} from "react-icons/fa";
import jsPDF from "jspdf";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import "../css/ClassTermStudents.css";
import LoadingPage from "../components/loading";
import html2canvas from "html2canvas";
import { toast } from "react-toastify";

const ClassTermStudents = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [modalOpen, setModalOpen] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState("");
  const [, setSelectedStudent] = useState(null); // InstStud from list
  const [reportData, setReportData] = useState(null); // payload from backend
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({
    current: 0,
    total: 0,
    label: "",
  });

  const token = useMemo(() => {
    const tokenString = localStorage.getItem("authToken");
    return tokenString ? JSON.parse(tokenString) : null;
  }, []);

  const instclassId = searchParams.get("instclassId");
  const doneTermId = searchParams.get("done_termId");

  // fetch students for the class/term
  useEffect(() => {
    const fetchStudents = async () => {
      if (!token) {
        navigate("/login");
        return;
      }
      try {
        const res = await fetch(
          `${API_URL}/class-term-students/?instclassId=${instclassId}&done_termId=${doneTermId}`,
          { headers: { Authorization: `Bearer ${token.access}` } }
        );
        const data = await res.json();
        if (res.ok) {
          setStudents(Array.isArray(data) ? data : []);
        } else {
          setError(data.error || "Failed to fetch students.");
        }
      } catch (err) {
        setError("Network error.");
      } finally {
        setLoading(false);
      }
    };
    fetchStudents();
  }, [instclassId, doneTermId, token, navigate]);

  // fetch report for a single student
  const openReportModal = async (stud) => {
    setSelectedStudent(stud);
    setReportData(null);
    setReportError("");
    setReportLoading(true);
    setModalOpen(true);
    try {
      const res = await fetch(
        `${API_URL}/students/${stud.id}/term/${doneTermId}/report-card/`,
        { headers: { Authorization: `Bearer ${token?.access}` } }
      );
      const payload = await res.json();
      if (!res.ok) throw new Error(payload?.error || "Unable to load report");
      setReportData(payload);
    } catch (e) {
      setReportError(e.message);
    } finally {
      setReportLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedStudent(null);
    setReportData(null);
    setReportError("");
    setReportLoading(false);
  };
  // PDF generator (single report) using jsPDF text layout (no DOM capture)
  const buildPDFBlob = (data) => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    let y = margin;

    const addLine = (text, opts = {}) => {
      const { size = 11, bold = false, gap = 6 } = opts;
      doc.setFont("helvetica", bold ? "bold" : "normal");
      doc.setFontSize(size);
      const lines = doc.splitTextToSize(text, pageWidth - margin * 2);
      lines.forEach((line) => {
        if (y + size > pageHeight - margin) {
          doc.addPage();
          y = margin;
        }
        doc.text(line, margin, y);
        y += size + gap;
      });
    };

    const addTableHeader = (cols) => {
      addLine(cols.join(" | "), { bold: true });
    };

    const addTableRow = (cols) => {
      addLine(cols.join(" | "));
    };

    const s = data.student || {};
    const t = data.term || {};

    // Header
    addLine("Student Report Card", { size: 18, bold: true, gap: 12 });
    addLine(`Student: ${s.name || s.inst_name || ""}`);
    addLine(`Email: ${s.email || ""}`);
    if (s.country) addLine(`Country: ${s.country}`);
    if (s.location) addLine(`Location: ${s.location}`);
    addLine(`Term: ${t.name || ""}`);
    addLine(`GPA: ${Number(data.gpa || 0).toFixed(2)}`, { gap: 12 });

    // Subjects
    const report = data.report || {};
    Object.keys(report).forEach((subject, idx) => {
      addLine(`${idx > 0 ? "\n" : ""}${subject}`, {
        size: 14,
        bold: true,
        gap: 8,
      });
      const entry = report[subject];
      addTableHeader(["Activity", "Score", "Weight", "Weighted"]);
      (entry.activities || []).forEach((a) => {
        addTableRow([
          `${a.activity_name}`,
          `${a.score}`,
          `${a.weight}`,
          `${a.weighted_score}`,
        ]);
      });
      addLine(`Total Weighted: ${entry.total_weighted || 0}`);
      addLine(`Letter Grade: ${entry.letter_grade || ""}`, { gap: 12 });
    });

    return doc.output("blob");
  };

  // CSV generator (single report)
  const buildCSV = (data) => {
    // data shape from backend
    const lines = [];
    const s = data.student || {};
    const t = data.term || {};
    lines.push(`Student ID,${s.id || ""}`);
    lines.push(`Institution Name,${s.inst_name || ""}`);
    lines.push(`Name,${s.name || ""}`);
    lines.push(`Email,${s.email || ""}`);
    lines.push(`Country,${s.country || ""}`);
    lines.push(`Location,${s.location || ""}`);
    lines.push(`Term,${t.name || ""}`);
    lines.push(`GPA,${Number(data.gpa || 0).toFixed(2)}`);
    lines.push("");

    // per-subject
    lines.push(
      "Subject,Activity,Score,Weight,Weighted Score,Subject Total,Letter Grade"
    );
    const report = data.report || {};
    Object.keys(report).forEach((subject) => {
      const entry = report[subject];
      const activities = entry.activities || [];
      if (activities.length === 0) {
        lines.push(
          `${subject},(none),,, ,${entry.total_weighted || 0},${
            entry.letter_grade || ""
          }`
        );
      } else {
        activities.forEach((a, idx) => {
          const base = idx === 0 ? `${entry.total_weighted || 0}` : "";
          const lg = idx === 0 ? `${entry.letter_grade || ""}` : "";
          lines.push(
            [
              subject,
              a.activity_name,
              a.score,
              a.weight,
              a.weighted_score,
              base,
              lg,
            ].join(",")
          );
        });
      }
    });

    return lines.join("\n");
  };

  // Bulk ZIP: fetch each student's report, then add each file (PDF or CSV) to a zip
  const downloadAllAsZip = async (format = "pdf") => {
    if (!students?.length) return;

    setBulkBusy(true);
    setBulkProgress({
      current: 0,
      total: students.length,
      label: "Starting…",
    });

    try {
      const zip = new JSZip();

      for (let i = 0; i < students.length; i++) {
        const stud = students[i];

        setBulkProgress({
          current: i + 1,
          total: students.length,
          label: stud.name || stud.studcode,
        });

        const res = await fetch(
          `${API_URL}/students/${stud.id}/term/${doneTermId}/report-card/`,
          { headers: { Authorization: `Bearer ${token?.access}` } }
        );

        const payload = await res.json();
        if (!res.ok) throw new Error(payload?.error || "Fetch failed");

        const baseName = `${(
          payload.student?.name ||
          payload.student?.inst_name ||
          stud.name ||
          stud.studcode ||
          "report"
        )
          .replace(/\s+/g, "_")
          .toLowerCase()}_${payload.term?.name || ""}_${stud.id}`;

        if (format === "csv") {
          const csv = buildCSV(payload);
          zip.file(`${baseName}.csv`, csv);
        } else {
          const pdfBlob = buildPDFBlob(payload);

          zip.file(`${baseName}.pdf`, pdfBlob); // 👈 simpler, no arrayBuffer needed
        }
      }

      const content = await zip.generateAsync({ type: "blob" });

      saveAs(content, `class_reports_${format}.zip`);
    } catch (e) {
      toast.error(e.message || "Failed to build ZIP");
    } finally {
      setBulkBusy(false);
      setBulkProgress({ current: 0, total: 0, label: "" });
    }
  };

  const downloadSinglePDF = async (data) => {
    try {
      const element = document.getElementById("report-page");
      if (!element) return;

      const oldHeight = element.style.height;
      const oldOverflow = element.style.overflow;

      element.style.height = "auto";
      element.style.overflow = "visible";


      // Capture the report container as canvas
      const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          windowWidth: element.scrollWidth,
          windowHeight: element.scrollHeight,
          width: element.scrollWidth,
          height: element.scrollHeight,
          scrollX: 0,
          scrollY: 0,
      });

      element.style.height = oldHeight;
      element.style.overflow = oldOverflow;

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pageWidth;
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      let heightLeft = pdfHeight;
      let position = 0;

      // Add first page
      pdf.addImage(imgData, "PNG", 0, position, pdfWidth, pdfHeight);
      heightLeft -= pageHeight;

      // Add extra pages if report is taller than A4
      while (heightLeft > 0) {
        position = heightLeft - pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, pdfWidth, pdfHeight);
        heightLeft -= pageHeight;
      }

      // Create file name
      const filename = `${(
        data.student?.name ||
        data.student?.inst_name ||
        "report"
      ).replace(/\s+/g, "_")}_${data.term?.name || ""}.pdf`;

      pdf.save(filename);
    } catch (err) {
      console.error("PDF download failed:", err);
    }
  };

 const downloadSingleCSV = (data) => {
   try {
     if (!data) return;

     const csv = buildCSV(data);

     const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
     const url = URL.createObjectURL(blob);

     const filename = `${(
       data.student?.name ||
       data.student?.inst_name ||
       "report"
     ).replace(/\s+/g, "_")}_${data.term?.name || ""}.csv`;

     const link = document.createElement("a");
     link.href = url;
     link.download = filename;

     document.body.appendChild(link);
     link.click();

     // cleanup
     document.body.removeChild(link);
     setTimeout(() => URL.revokeObjectURL(url), 100);
   } catch (err) {
     console.error("CSV download failed:", err);
   }
 };
  // ── Loading & Error States ───────────────────────────────────────────────────
  if (loading) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background:
            "linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 50%, #e1f5fe 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            textAlign: "center",
            border: "1px solid rgba(239,68,68,0.2)",
            background: "#fff",
            maxWidth: 360,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              background: "rgba(239,68,68,0.1)",
              border: "1px solid rgba(239,68,68,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <FaExclamationTriangle size={20} color="#ef4444" />
          </Box>
          <Typography sx={{ fontWeight: 800, color: "#0c1a2e", mb: 0.5 }}>
            Something went wrong
          </Typography>
          <Typography variant="caption" sx={{ color: "#94a3b8" }}>
            {error}
          </Typography>
        </Paper>
      </Box>
    );
  }

  // ── Main Page ────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 50%, #e1f5fe 100%)",
        py: 4,
        px: { xs: 2, md: 4 },
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 3,
          borderRadius: 3,
          background: "#fff",
          border: "1px solid rgba(2,136,209,0.15)",
          boxShadow: "0 2px 12px rgba(2,136,209,0.08)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "center" }}
          spacing={2}
          gap={1}
        >
          <IconButton
            onClick={() => navigate(-1)}
            aria-label="Go Back"
            sx={{
              color: "#0288D1",
              background: "rgba(2,136,209,0.08)",
              border: "1px solid rgba(2,136,209,0.2)",
              alignSelf: "flex-start",
              "&:hover": { background: "rgba(2,136,209,0.15)" },
            }}
          >
            <FaArrowLeft size={16} />
          </IconButton>

          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                color: "#0c1a2e",
                letterSpacing: "-0.5px",
              }}
            >
              Class Students
            </Typography>
            <Typography variant="caption" sx={{ color: "#64748b" }}>
              {students.length} student{students.length !== 1 ? "s" : ""}{" "}
              enrolled
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5} flexWrap="wrap" gap={1}>
            <Button
              onClick={() => downloadAllAsZip("pdf")}
              disabled={bulkBusy}
              variant="contained"
              size="small"
              startIcon={bulkBusy ? null : <FaFilePdf size={12} />}
              sx={{
                borderRadius: 99,
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.78rem",
                px: 2,
                background: bulkBusy
                  ? "#e2e8f0"
                  : "linear-gradient(135deg, #ef4444, #f87171)",
                color: bulkBusy ? "#94a3b8" : "#fff",
                boxShadow: bulkBusy
                  ? "none"
                  : "0 4px 12px rgba(239,68,68,0.25)",
                "&:hover": {
                  background: bulkBusy
                    ? "#e2e8f0"
                    : "linear-gradient(135deg, #dc2626, #ef4444)",
                },
                "&:disabled": { color: "#94a3b8", background: "#e2e8f0" },
              }}
            >
              {bulkBusy ? (
                <CircularProgress size={14} sx={{ color: "#94a3b8" }} />
              ) : (
                "ZIP (PDF)"
              )}
            </Button>

            <Button
              onClick={() => downloadAllAsZip("csv")}
              disabled={bulkBusy}
              variant="outlined"
              size="small"
              startIcon={<FaFileCsv size={12} />}
              sx={{
                borderRadius: 99,
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.78rem",
                px: 2,
                borderColor: "rgba(5,150,105,0.3)",
                color: "#059669",
                "&:hover": {
                  borderColor: "#059669",
                  background: "rgba(5,150,105,0.05)",
                },
              }}
            >
              ZIP (CSV)
            </Button>
          </Stack>
        </Stack>

        {/* Bulk progress bar */}
        {bulkBusy && (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" justifyContent="space-between" mb={0.5}>
              <Typography
                variant="caption"
                sx={{ color: "#64748b", fontWeight: 600 }}
              >
                {bulkProgress.label}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#0288D1", fontWeight: 700 }}
              >
                {bulkProgress.current}/{bulkProgress.total}
              </Typography>
            </Stack>
            <Box
              sx={{
                height: 6,
                borderRadius: 99,
                background: "#e2e8f0",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  borderRadius: 99,
                  background: "linear-gradient(90deg, #0288D1, #0ea5e9)",
                  width: `${
                    (bulkProgress.current / (bulkProgress.total || 1)) * 100
                  }%`,
                  transition: "width 0.3s ease",
                  boxShadow: "0 0 8px rgba(2,136,209,0.4)",
                }}
              />
            </Box>
          </Box>
        )}
      </Paper>

      {/* ── Students Grid ───────────────────────────────────────────────────── */}
      {students.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 10 }}>
          <FaUserGraduate size={40} color="#bae6fd" />
          <Typography sx={{ color: "#94a3b8", mt: 2, fontWeight: 600 }}>
            No students found.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {students.map((student) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={student.id}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  background: "#fff",
                  border: "1px solid #e2e8f0",
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: "rgba(2,136,209,0.35)",
                    boxShadow: "0 4px 16px rgba(2,136,209,0.12)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                {/* Avatar */}
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    background:
                      "linear-gradient(135deg, rgba(2,136,209,0.12), rgba(14,165,233,0.2))",
                    border: "1px solid rgba(2,136,209,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1.5,
                  }}
                >
                  <FaUserGraduate size={20} color="#0288D1" />
                </Box>

                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#0c1a2e",
                    fontSize: "0.88rem",
                    textTransform: "capitalize",
                    mb: 0.25,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={student.name}
                >
                  {student.name}
                </Typography>

                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    color: "#94a3b8",
                    fontFamily: "monospace",
                    letterSpacing: "0.5px",
                    mb: 2,
                  }}
                >
                  {student.studcode}
                </Typography>

                <Button
                  onClick={() => openReportModal(student)}
                  variant="outlined"
                  size="small"
                  fullWidth
                  startIcon={<FaFileAlt size={11} />}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    borderColor: "rgba(2,136,209,0.3)",
                    color: "#0288D1",
                    "&:hover": {
                      borderColor: "#0288D1",
                      background: "rgba(2,136,209,0.05)",
                    },
                  }}
                >
                  View Report
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Report Modal ────────────────────────────────────────────────────── */}
      {modalOpen && (
        <Box
          onClick={closeModal}
          sx={{
            position: "fixed",
            inset: 0,
            background: "rgba(12,26,46,0.5)",
            backdropFilter: "blur(6px)",
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
        >
          <Paper
            onClick={(e) => e.stopPropagation()}
            elevation={0}
            sx={{
              width: "100%",
              maxWidth: 680,
              maxHeight: "90vh",
              borderRadius: 3,
              background: "#fff",
              border: "1px solid rgba(2,136,209,0.15)",
              boxShadow: "0 24px 60px rgba(12,26,46,0.2)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* Modal header */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                px: 3,
                py: 2,
                borderBottom: "1px solid #e2e8f0",
                background:
                  "linear-gradient(135deg, rgba(2,136,209,0.05), rgba(14,165,233,0.02))",
                flexShrink: 0,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    background: "linear-gradient(135deg, #0288D1, #0ea5e9)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 10px rgba(2,136,209,0.3)",
                  }}
                >
                  <FaFileAlt size={14} color="#fff" />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      color: "#0c1a2e",
                      fontSize: "0.95rem",
                    }}
                  >
                    Report Card
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#64748b" }}>
                    Academic performance summary
                  </Typography>
                </Box>
              </Stack>
              <IconButton
                onClick={closeModal}
                size="small"
                sx={{
                  color: "#64748b",
                  background: "#f1f5f9",
                  border: "1px solid #e2e8f0",
                  borderRadius: 1.5,
                  "&:hover": { background: "#e2e8f0" },
                }}
              >
                <FaTimes size={13} />
              </IconButton>
            </Stack>

            {/* Modal body */}
            <Box
              sx={{ flex: 1, overflowY: "auto", p: 3 }}
              // id="report-card"
              id="report-page"
            >
              {reportLoading && (
                <Stack alignItems="center" py={6} spacing={2}>
                  <Box sx={{ position: "relative", width: 60, height: 60 }}>
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        borderRadius: "50%",
                        border: "3px solid transparent",
                        borderTopColor: "#0288D1",
                        borderRightColor: "#0288D1",
                        animation: "spin 1.2s linear infinite",
                        "@keyframes spin": {
                          "0%": { transform: "rotate(0deg)" },
                          "100%": { transform: "rotate(360deg)" },
                        },
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 8,
                        borderRadius: "50%",
                        border: "2px solid transparent",
                        borderTopColor: "#7c3aed",
                        borderLeftColor: "#7c3aed",
                        animation: "spinReverse 1s linear infinite",
                        "@keyframes spinReverse": {
                          "0%": { transform: "rotate(0deg)" },
                          "100%": { transform: "rotate(-360deg)" },
                        },
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 18,
                        borderRadius: "50%",
                        background: "linear-gradient(135deg, #0288D1, #7c3aed)",
                        animation: "pulse 1.4s ease-in-out infinite",
                        "@keyframes pulse": {
                          "0%, 100%": { transform: "scale(1)", opacity: 1 },
                          "50%": { transform: "scale(0.75)", opacity: 0.6 },
                        },
                      }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ color: "#64748b" }}>
                    Loading report…
                  </Typography>
                </Stack>
              )}

              {reportError && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    textAlign: "center",
                    border: "1px solid rgba(239,68,68,0.2)",
                    background: "rgba(239,68,68,0.03)",
                  }}
                >
                  <FaExclamationTriangle size={24} color="#ef4444" />
                  <Typography
                    sx={{
                      color: "#ef4444",
                      fontWeight: 600,
                      mt: 1,
                      fontSize: "0.88rem",
                    }}
                  >
                    {reportError}
                  </Typography>
                </Paper>
              )}

              {!reportLoading && !reportError && reportData && (
                <Stack spacing={3}>
                  {/* Student identity */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: 2.5,
                      background:
                        "linear-gradient(135deg, rgba(2,136,209,0.04), rgba(14,165,233,0.02))",
                      border: "1px solid rgba(2,136,209,0.15)",
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2}
                      alignItems={{ sm: "center" }}
                      justifyContent="space-between"
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        {/* Avatar */}
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: "50%",
                            overflow: "hidden",
                            border: "2px solid rgba(2,136,209,0.3)",
                            flexShrink: 0,
                            background:
                              "linear-gradient(135deg, #0288D1, #0ea5e9)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {reportData.student?.profile_pic ? (
                            <img
                              src={reportData.student.profile_pic}
                              alt="Profile"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <Typography
                              sx={{
                                fontWeight: 800,
                                color: "#fff",
                                fontSize: "1.3rem",
                              }}
                            >
                              {(reportData.student?.name || "?")
                                .slice(0, 1)
                                .toUpperCase()}
                            </Typography>
                          )}
                        </Box>
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 800,
                              color: "#0c1a2e",
                              fontSize: "1rem",
                            }}
                          >
                            {reportData.student?.name ||
                              reportData.student?.inst_name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "#64748b", display: "block" }}
                          >
                            {reportData.student?.email}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "#94a3b8" }}
                          >
                            {reportData.student?.country}
                            {reportData.student?.location
                              ? ` • ${reportData.student.location}`
                              : ""}
                          </Typography>
                        </Box>
                      </Stack>

                      {/* Meta chips */}
                      <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        gap={1}
                      >
                        <Box sx={{ textAlign: "center" }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#94a3b8",
                              display: "block",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              fontSize: "0.6rem",
                              fontWeight: 700,
                            }}
                          >
                            Term
                          </Typography>
                          <Chip
                            label={reportData.term?.name}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              background: "rgba(2,136,209,0.1)",
                              color: "#0288D1",
                              border: "1px solid rgba(2,136,209,0.2)",
                              fontSize: "0.72rem",
                            }}
                          />
                        </Box>
                        <Box sx={{ textAlign: "center" }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#94a3b8",
                              display: "block",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              fontSize: "0.6rem",
                              fontWeight: 700,
                            }}
                          >
                            GPA
                          </Typography>
                          <Chip
                            label={Number(reportData.gpa || 0).toFixed(2)}
                            size="small"
                            sx={{
                              fontWeight: 800,
                             
                              border: "none",
                              fontSize: "0.72rem",
                            }}
                          />
                        </Box>
                        <Box sx={{ textAlign: "center" }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#94a3b8",
                              display: "block",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              fontSize: "0.6rem",
                              fontWeight: 700,
                            }}
                          >
                            Position
                          </Typography>
                          <Chip
                            label={`#${Number(reportData.position)}`}
                            size="small"
                            sx={{
                              fontWeight: 800,
                              background: "rgba(124,58,237,0.1)",
                              color: "#7c3aed",
                              border: "1px solid rgba(124,58,237,0.2)",
                              fontSize: "0.72rem",
                            }}
                          />
                        </Box>
                      </Stack>
                    </Stack>

                    {/* Student name + class row */}
                    <Divider
                      sx={{ my: 2, borderColor: "rgba(2,136,209,0.1)" }}
                    />
                    <Stack direction="row" spacing={3} flexWrap="wrap" gap={1}>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#94a3b8",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            fontSize: "0.62rem",
                            fontWeight: 700,
                          }}
                        >
                          Student Name
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: "#0c1a2e",
                            fontSize: "0.85rem",
                            textTransform: "capitalize",
                          }}
                        >
                          {reportData.inststud}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#94a3b8",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            fontSize: "0.62rem",
                            fontWeight: 700,
                          }}
                        >
                          Class
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: "#0c1a2e",
                            fontSize: "0.85rem",
                          }}
                        >
                          {reportData.inststudclass}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>

                  {/* Subjects */}
                  <Stack spacing={2}>
                    {Object.keys(reportData.report || {}).map((subject) => {
                      const entry = reportData.report[subject];
                      return (
                        <Paper
                          key={subject}
                          elevation={0}
                          sx={{
                            borderRadius: 2.5,
                            border: "1px solid #e2e8f0",
                            overflow: "hidden",
                            background: "#fff",
                          }}
                        >
                          {/* Subject header */}
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{
                              px: 2.5,
                              py: 1.5,
                              background:
                                "linear-gradient(135deg, #f8fafc, #f1f5f9)",
                              borderBottom: "1px solid #e2e8f0",
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1.5}
                            >
                              <Box
                                sx={{
                                  width: 30,
                                  height: 30,
                                  borderRadius: 1.5,
                                  background:
                                    "linear-gradient(135deg, rgba(2,136,209,0.12), rgba(14,165,233,0.18))",
                                  border: "1px solid rgba(2,136,209,0.2)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <FaGraduationCap size={13} color="#0288D1" />
                              </Box>
                              <Typography
                                sx={{
                                  fontWeight: 800,
                                  color: "#0c1a2e",
                                  fontSize: "0.88rem",
                                  textTransform: "capitalize",
                                }}
                              >
                                {subject}
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1}>
                              <Chip
                                label={`Total: ${entry.total_weighted}`}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: "0.65rem",
                                  fontWeight: 700,
                                  background: "rgba(2,136,209,0.1)",
                                  color: "#0288D1",
                                  border: "1px solid rgba(2,136,209,0.2)",
                                }}
                              />
                              <Chip
                                label={`Grade: ${entry.letter_grade}`}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: "0.65rem",
                                  fontWeight: 800,
                                  background:
                                    "linear-gradient(135deg, #059669, #10b981)",
                                  color: "#fff",
                                  border: "none",
                                }}
                              />
                            </Stack>
                          </Stack>

                          {/* Activities table */}
                          <Box sx={{ overflowX: "auto" }}>
                            <table
                              style={{
                                width: "100%",
                                borderCollapse: "collapse",
                              }}
                            >
                              <thead>
                                <tr style={{ background: "#f8fafc" }}>
                                  {[
                                    "Task",
                                    "Marks",
                                    "Percentage",
                                    "Grade Points",
                                  ].map((h) => (
                                    <th
                                      key={h}
                                      style={{
                                        padding: "8px 16px",
                                        textAlign: "left",
                                        fontSize: "0.65rem",
                                        fontWeight: 700,
                                        color: "#64748b",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.6px",
                                        borderBottom: "1px solid #e2e8f0",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {h}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {(entry.activities || []).length === 0 ? (
                                  <tr>
                                    <td
                                      colSpan={4}
                                      style={{
                                        padding: "16px",
                                        textAlign: "center",
                                        color: "#94a3b8",
                                        fontSize: "0.82rem",
                                      }}
                                    >
                                      No activities recorded
                                    </td>
                                  </tr>
                                ) : (
                                  (entry.activities || []).map((a, idx) => (
                                    <tr
                                      key={idx}
                                      style={{
                                        background:
                                          idx % 2 === 0 ? "#fff" : "#f8fafc",
                                      }}
                                    >
                                      <td
                                        style={{
                                          padding: "10px 16px",
                                          fontSize: "0.82rem",
                                          color: "#0c1a2e",
                                          fontWeight: 600,
                                          borderBottom: "1px solid #f1f5f9",
                                        }}
                                      >
                                        {a.activity_name}
                                      </td>
                                      <td
                                        style={{
                                          padding: "10px 16px",
                                          fontSize: "0.82rem",
                                          color: "#334155",
                                          borderBottom: "1px solid #f1f5f9",
                                        }}
                                      >
                                        {a.score}
                                      </td>
                                      <td
                                        style={{
                                          padding: "10px 16px",
                                          borderBottom: "1px solid #f1f5f9",
                                        }}
                                      >
                                        <Chip
                                          label={`${a.weight}%`}
                                          size="small"
                                          sx={{
                                            height: 18,
                                            fontSize: "0.62rem",
                                            fontWeight: 700,
                                            background: "rgba(2,136,209,0.08)",
                                            color: "#0288D1",
                                            border: "none",
                                          }}
                                        />
                                      </td>
                                      <td
                                        style={{
                                          padding: "10px 16px",
                                          borderBottom: "1px solid #f1f5f9",
                                        }}
                                      >
                                        <Chip
                                          label={a.weighted_score}
                                          size="small"
                                          sx={{
                                            height: 18,
                                            fontSize: "0.62rem",
                                            fontWeight: 700,
                                            background: "rgba(5,150,105,0.1)",
                                            color: "#059669",
                                            border: "none",
                                          }}
                                        />
                                      </td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </Box>
                        </Paper>
                      );
                    })}
                  </Stack>

                  {/* Signature */}
                  {reportData.student?.signature && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: "1px solid #e2e8f0",
                        background: "#f8fafc",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#94a3b8",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          display: "block",
                          mb: 1,
                        }}
                      >
                        Admin Signature
                      </Typography>
                      <img
                        src={reportData.student.signature}
                        alt="Signature"
                        style={{ maxHeight: 60, objectFit: "contain" }}
                      />
                    </Paper>
                  )}
                </Stack>
              )}
            </Box>

            {/* Modal footer */}
            {!reportLoading && !reportError && reportData && (
              <Stack
                direction="row"
                spacing={1.5}
                sx={{
                  px: 3,
                  py: 2,
                  borderTop: "1px solid #e2e8f0",
                  background: "#f8fafc",
                  flexShrink: 0,
                }}
              >
                <Button
                  onClick={() => downloadSinglePDF(reportData)}
                  variant="contained"
                  startIcon={<FaFilePdf size={12} />}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.82rem",
                    background: "linear-gradient(135deg, #ef4444, #f87171)",
                    boxShadow: "0 4px 12px rgba(239,68,68,0.25)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #dc2626, #ef4444)",
                    },
                  }}
                >
                  Download PDF
                </Button>{" "}
                <Button
                  onClick={() => downloadSingleCSV(reportData)}
                  variant="outlined"
                  startIcon={<FaFileCsv size={12} />}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.82rem",
                    borderColor: "rgba(5,150,105,0.3)",
                    color: "#059669",
                    "&:hover": {
                      borderColor: "#059669",
                      background: "rgba(5,150,105,0.05)",
                    },
                  }}
                >
                  Download CSV
                </Button>
              </Stack>
            )}
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default ClassTermStudents;
